import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, MessageCircleQuestion, ClipboardCheck, ArrowRight, Crown } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { API_BASE } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { mediaUrl } from '../lib/mediaUrl';

const API = `${API_BASE}/api`;

function Stat({ icon: Icon, value, label, tone }) {
  return (
    <div className="flex items-center gap-2 text-xs text-zinc-400">
      <Icon className={`h-4 w-4 ${tone}`} /><span className="text-white font-semibold">{value}</span>{label}
    </div>
  );
}

export default function InstructorHubPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    axios.get(`${API}/instructor/programmes`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRows(r.data.programmes || []))
      .catch(() => { toast.error('Could not load your programmes'); setRows([]); });
  }, [token]);

  return (
    <Layout pageTitle="Instructor Hub" breadcrumbItems={[{ label: 'Instructor Hub' }]}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="instructor-hub">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-400 mb-2">Instructor Hub</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-2">Welcome, {user?.full_name?.split(' ')[0] || 'Instructor'}</h1>
        <p className="text-zinc-400 mb-10 max-w-2xl">Review course content, mark submissions, track progress and answer learner questions for the programmes you're assigned to.</p>
        {rows === null ? <div className="h-40 rounded-2xl bg-zinc-900 animate-pulse" /> : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-zinc-800 p-12 text-center" data-testid="instructor-hub-empty">
            <p className="text-zinc-300 font-medium">No programmes assigned yet</p>
            <p className="text-sm text-zinc-500 mt-1">A super admin will assign you to a programme or course.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {rows.map((p) => (
              <button key={p.programme_id} onClick={() => navigate(`/instructor/programmes/${p.programme_id}`)} data-testid={`instructor-programme-card-${p.programme_id}`}
                className="group text-left rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden hover:border-teal-500/50 transition-colors">
                <div className="relative h-32 bg-zinc-800">
                  {p.thumbnail_url && <img src={mediaUrl(p.thumbnail_url)} alt="" className="w-full h-full object-cover opacity-70" />}
                  <span className="absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-zinc-950/80 text-teal-300 border border-teal-500/30">
                    {p.my_role === 'lead' ? <><Crown className="h-3 w-3" />Lead instructor</> : `Course instructor · ${p.my_course_count} course(s)`}
                  </span>
                </div>
                <div className="p-5">
                  <p className="text-white font-semibold line-clamp-2 mb-4">{p.programme_name}</p>
                  <div className="flex flex-wrap gap-4">
                    <Stat icon={Users} value={p.learner_count} label="learners" tone="text-sky-400" />
                    <Stat icon={MessageCircleQuestion} value={p.open_questions} label="questions waiting" tone="text-amber-400" />
                    <Stat icon={ClipboardCheck} value={p.to_mark} label="to mark" tone="text-emerald-400" />
                  </div>
                  <span className="mt-4 inline-flex items-center text-sm text-teal-400 group-hover:translate-x-1 transition-transform">Open <ArrowRight className="h-4 w-4 ml-1" /></span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
